import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import siteSetting from "../../config/env";
import { marketingApiService } from "../marketing-v2/services/marketingApiService";
import SlidePanel from "../shared/SlidePanel";
import "./MarketingPanels.css";

// The email templates are Unlayer designs built at 700px with a desktop layout
// that only applies above a 720px breakpoint (below it the columns stack, which
// splays the footer's App Store / Google Play badges apart). So we render at a
// native width just above that breakpoint to keep the intended desktop layout,
// then scale to fit the panel (works at any width).
const EMAIL_NATIVE_WIDTH = 730;
// Fallback content height (px at 600px width) when we can't measure the email.
// The standard templates are cross-origin, so the browser blocks reading their
// height; EmailPreviewPanel passes a measured estimate via `nativeHeight`. This
// is only used if none is provided.
const DEFAULT_EMAIL_HEIGHT = 1100;

export function EmailFrame({ src, html, nativeHeight }) {
  const wrapRef = useRef(null);
  const iframeRef = useRef(null);
  const [scale, setScale] = useState(1);
  // Real content height of the email at native width. For same-origin content
  // (custom emails written via doc.write) we measure it; for cross-origin
  // templates we keep the supplied estimate (reading their DOM is blocked).
  const [contentHeight, setContentHeight] = useState(nativeHeight || DEFAULT_EMAIL_HEIGHT);

  useLayoutEffect(() => {
    setContentHeight(nativeHeight || DEFAULT_EMAIL_HEIGHT);
  }, [nativeHeight]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => setScale(Math.min(1, el.clientWidth / EMAIL_NATIVE_WIDTH));
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener("resize", update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, []);

  // Read the email's true height when we're allowed to (same-origin). Cross-origin
  // access throws, so we silently keep the estimate.
  const measure = () => {
    try {
      const frame = iframeRef.current;
      const doc = frame && (frame.contentDocument || frame.contentWindow.document);
      if (!doc || !doc.body) return;
      const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      if (h > 0) setContentHeight(h);
    } catch (e) {
      /* cross-origin email: keep the supplied estimate */
    }
  };

  useEffect(() => {
    if (html && iframeRef.current) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      measure();
      // Re-measure once images/fonts have settled so the height is accurate.
      const t = setTimeout(measure, 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [html]);

  const s = scale || 0.0001;
  return (
    <div
      ref={wrapRef}
      style={{
        width: "100%",
        // Reserve the scaled height so the email shows in full and the panel
        // body scrolls, instead of cramming the content into a fixed box.
        height: `${Math.round(contentHeight * s)}px`,
        overflow: "hidden",
        position: "relative",
        background: "#fff",
      }}
    >
      <iframe
        ref={iframeRef}
        title="Email preview"
        src={src || undefined}
        frameBorder="0"
        scrolling="no"
        onLoad={measure}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${EMAIL_NATIVE_WIDTH}px`,
          height: `${contentHeight}px`,
          border: "none",
          transform: `scale(${s})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
}

/**
 * Shared building blocks for the SlidePanel-based marketing automation editors.
 * Keeps each editor panel small and visually consistent with the rest of the new UI.
 */

// SMS preview copy, keyed by automation kind.
export const getSmsText = (kind) => {
  const t = siteSetting?.SMSText || {};
  switch (kind) {
    case "reminder": return t.reminder;
    case "googleReview": return t.googleReview;
    case "newBooking": return t.newBooking;
    case "updatedBooking": return t.updatedBooking;
    case "noShow": return t.noShow;
    case "cancelledBooking": return t.cancelledBooking;
    case "thankYouForVisiting": return t.thankYouForVisiting;
    case "thankYouForTipping": return t.thankYouForTipping;
    case "reminderToRebook": return t.reminderToRebook;
    case "happyBirthday": return t.happyBirthday;
    default: return t.reminder;
  }
};

// Email preview template URL, keyed by the preview "type" label used across the app.
const EMAIL_TEMPLATE_FILES = {
  "Reminder": "Email%20Reminder.html",
  "Update Booking": "Updated%20Booking%20email.html",
  "Cancelled booking": "cancelled%20booking.html",
  "Happy birthday": "happy%20birthday.html",
  "New Booking": "new%20booking%20email.html",
  "No-show": "no%20show%20email.html",
  "Reminder to rebook": "reminder%20to%20rebook.html",
  "Thank you for tipping": "thank%20you%20for%20tipping.html",
  "Thank you after visiting": "thank%20you%20for%20visiting.html",
  "Google Review Booster": "Google%20Review%20Booster.html",
};

export const getEmailPreviewUrl = (type) => {
  const file = EMAIL_TEMPLATE_FILES[type];
  if (!file) return null;
  return siteSetting?.api?.v2BaseUrl + "/email/" + file;
};

// Natural content height (px) of each template at the EMAIL_NATIVE_WIDTH above.
// The templates are served from a different origin, so the browser blocks
// reading their height in the preview; these are measured values used to
// reserve the right amount of space (with a little headroom). Slightly off
// just adds/removes a thin margin; it can never cram rows on top of each other.
const EMAIL_TEMPLATE_HEIGHTS = {
  "Reminder": 1035,
  "Update Booking": 1055,
  "Cancelled booking": 975,
  "Happy birthday": 945,
  "New Booking": 1030,
  "No-show": 1035,
  "Reminder to rebook": 965,
  "Thank you for tipping": 860,
  "Thank you after visiting": 915,
  "Google Review Booster": 565,
};

export const getEmailPreviewHeight = (type) => EMAIL_TEMPLATE_HEIGHTS[type] || 1100;

// Preview mapping for "Updates" (booking) automations, keyed by the item's `type`.
export const BOOKING_PREVIEW = {
  1: { sms: "newBooking", email: "New Booking" },
  2: { sms: "updatedBooking", email: "Update Booking" },
  3: { sms: "noShow", email: "No-show" },
  4: { sms: "cancelledBooking", email: "Cancelled booking" },
  5: { sms: "thankYouForVisiting", email: "Thank you after visiting" },
  6: { sms: "thankYouForTipping", email: "Thank you for tipping" },
};

// Preview mapping for "Sales" automations, keyed by the item's `type`.
export const SALES_PREVIEW = {
  1: { sms: "reminderToRebook", email: "Reminder to rebook" },
  2: { sms: "happyBirthday", email: "Happy birthday" },
};

// Shared react-select styling so dropdowns match the .sp-input / .sp-select tokens
// (44px tall, 10px radius, #fafafa fill, charcoal focus + selected, no bright-blue).
export const marketingSelectProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
  theme: (theme) => ({
    ...theme,
    colors: { ...theme.colors, primary: "#29282D", primary25: "#f0f0f2", primary50: "#e5e7eb" },
  }),
  styles: {
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      height: 44,
      backgroundColor: state.isFocused ? "#fff" : "#fafafa",
      borderColor: state.isFocused ? "#1a1a1a" : "#e5e5e5",
      borderRadius: 10,
      boxShadow: "none",
      fontSize: 14,
      cursor: "pointer",
      transition: "border-color 0.15s, background 0.15s",
      "&:hover": { borderColor: state.isFocused ? "#1a1a1a" : "#e5e5e5" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 14px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#1a1a1a" }),
    singleValue: (base) => ({ ...base, color: "#1a1a1a", fontSize: 14 }),
    placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: 14 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, color: "#6b7280", padding: "0 10px" }),
    menu: (base) => ({
      ...base,
      borderRadius: 10,
      overflow: "hidden",
      border: "1px solid #f0f0f2",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.10)",
      marginTop: 6,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100012 }),
    menuList: (base) => ({ ...base, padding: 4 }),
    option: (base, state) => ({
      ...base,
      borderRadius: 8,
      padding: "10px 12px",
      fontSize: 14,
      cursor: "pointer",
      color: state.isSelected ? "#fff" : "#29282D",
      backgroundColor: state.isSelected ? "#29282D" : state.isFocused ? "#f0f0f2" : "#fff",
      ":active": { backgroundColor: state.isSelected ? "#29282D" : "#e5e7eb" },
    }),
  },
};

// Enable/disable toggle, styled to match the Services (.sep-toggle) switch.
export function ChannelToggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      className={`mkt-toggle${checked ? " mkt-toggle--on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span className="mkt-toggle-knob" />
    </button>
  );
}

// A single channel (SMS or Email) card with toggle, description and inline preview.
export function ChannelCard({
  title,
  cost,
  description,
  enabled,
  onToggle,
  previewLabel,
  onPreview,
  previewOpen,
  preview,
}) {
  return (
    <div className="mep-channel">
      <div className="mep-channel-head">
        <div>
          <h4 className="mep-channel-title">{title}</h4>
          {cost ? <div className="mep-channel-cost">{cost}</div> : null}
        </div>
        <ChannelToggle checked={enabled} onChange={onToggle} />
      </div>
      {description ? <p className="mep-channel-desc">{description}</p> : null}
      {onPreview ? (
        <div>
          <button type="button" className="mep-preview-link" onClick={onPreview}>
            {previewOpen ? "Hide preview" : previewLabel}
          </button>
        </div>
      ) : null}
      {previewOpen && preview ? <div className="mep-preview">{preview}</div> : null}
    </div>
  );
}

// Inline SMS preview bubble.
export function SmsPreview({ text }) {
  return (
    <div>
      <div className="mep-sms-bubble">{text}</div>
      <p className="mep-sms-meta">(No Reply)</p>
    </div>
  );
}

// SMS + Email channel cards.
// SMS shows an inline message bubble; Email opens a full stacked preview panel.
export function ChannelCards({
  smsTitle = "SMS",
  emailTitle = "Email",
  smsDesc,
  emailDesc,
  statusSMS,
  statusEmail,
  onToggleSms,
  onToggleEmail,
  smsText,
  emailType,
}) {
  const [smsOpen, setSmsOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  return (
    <>
      <ChannelCard
        title={smsTitle}
        cost="£0.04 / message"
        description={smsDesc}
        enabled={statusSMS}
        onToggle={onToggleSms}
        previewLabel="Preview SMS"
        onPreview={() => setSmsOpen((o) => !o)}
        previewOpen={smsOpen}
        preview={<SmsPreview text={smsText} />}
      />
      <ChannelCard
        title={emailTitle}
        cost="Free"
        description={emailDesc}
        enabled={statusEmail}
        onToggle={onToggleEmail}
        previewLabel="Preview Email"
        onPreview={() => setEmailPreviewOpen(true)}
        previewOpen={false}
        preview={null}
      />
      {emailPreviewOpen && (
        <EmailPreviewPanel type={emailType} onClose={() => setEmailPreviewOpen(false)} />
      )}
    </>
  );
}

// Full email preview in a stacked SlidePanel (slides over the editor / up on mobile).
export function EmailPreviewPanel({ type, onClose }) {
  const dispatch = useDispatch();
  // Prefer the backend's real rendered template (what it actually sends);
  // the static files under {v2BaseUrl}/email/ are stale snapshots from 2024
  // and only serve as the fallback until the preview endpoint responds.
  const [realHtml, setRealHtml] = useState(null);
  useEffect(() => {
    let alive = true;
    setRealHtml(null);
    marketingApiService.getEmailTemplatePreview(dispatch, { type })
      .then((res) => { if (alive && res && res.html) setRealHtml(res.html); })
      .catch(() => {});
    return () => { alive = false; };
  }, [type, dispatch]);

  const url = getEmailPreviewUrl(type);
  return (
    <SlidePanel title="Email Preview" onClose={onClose} width={640} zIndex={100010}>
      {realHtml ? (
        <EmailFrame html={realHtml} />
      ) : url ? (
        <EmailFrame src={url} nativeHeight={getEmailPreviewHeight(type)} />
      ) : (
        <p className="mep-channel-desc">Preview unavailable.</p>
      )}
    </SlidePanel>
  );
}
